/**
 * Follow-Up Tracker bucket definitions.
 *
 * Accounts are sorted into these buckets by how many days have passed
 * since their last depletion. Extracted out of `FollowUpTracker` so the
 * User Guide can document the exact buckets the product renders,
 * instead of a copy that drifts.
 *
 * Consumed by:
 *   - `components/FollowUpTracker.tsx` (the dashboard + rep surfaces)
 *   - `pages/UserGuidePage.tsx` (the manual + its PDF)
 */

export interface BucketMeta {
  /** Short verb for what to do with accounts in this bucket. */
  action: string;
  /** Plain-English restatement of the recency window. */
  timeLabel: string;
  /** Guidance on how to work the bucket. */
  desc: string;
}

export const BUCKET_META: Record<string, BucketMeta> = {
  '0-30 days': {
    action: 'Nurture',
    timeLabel: 'Ordered in the last 30 days',
    desc: 'Bought recently — send a thank-you, suggest complementary products to expand their shelf.',
  },
  '30-60 days': {
    action: 'Check In',
    timeLabel: 'Last order was 1–2 months ago',
    desc: 'Likely running low — reach out with availability updates or seasonal promos to prompt a reorder.',
  },
  '60-90 days': {
    action: 'Re-engage',
    timeLabel: 'Last order was 2–3 months ago',
    desc: 'Purchasing has slowed — schedule a call or visit, share new releases or tasting opportunities.',
  },
  '90-120 days': {
    action: 'Win Back',
    timeLabel: 'Last order was 3–4 months ago',
    desc: "Going cold — offer incentives or case deals, understand what's keeping them from reordering.",
  },
  '120+ days': {
    action: 'At Risk',
    timeLabel: 'No order in over 4 months',
    desc: 'Dormant account at high churn risk — needs urgent outreach before the relationship is lost.',
  },
  // Synthetic bucket used by the Field Sales surface — a single
  // ranked list combining the rep's most pressing accounts across
  // every real bucket.  Same row/table format as the standard
  // buckets so reps see a consistent shape.
  "Today's Plan": {
    action: 'Top picks',
    timeLabel: 'Ranked across every bucket — pinned, high-yield, declining first',
    desc: 'The accounts the system thinks need your attention today, surfaced from across every bucket and ordered by likelihood of impact.',
  },
};

/** The five real recency buckets, oldest-activity last. */
export const BUCKET_ORDER = ['0-30 days', '30-60 days', '60-90 days', '90-120 days', '120+ days'];

/**
 * Compact time-axis labels — abbreviated copies of `BUCKET_META.timeLabel`
 * so the labels under the pipeline bar fit on a single line at any
 * reasonable viewport width.
 */
export const SHORT_BUCKET_LABEL: Record<string, string> = {
  '0-30 days': 'Last 30 days',
  '30-60 days': '1–2 months ago',
  '60-90 days': '2–3 months ago',
  '90-120 days': '3–4 months ago',
  '120+ days': 'Over 4 months',
};
